/**
 * Iter330 · Multi-select combobox — searchable dropdown with checkboxes,
 * chips, "select all/clear", "+ Add new". Used across Leads Finder and
 * Lead Discovery so the same taxonomy propagates everywhere.
 *
 * Iter333/334 · Mobile hardening
 *   • Solid background (was broken `bg-[var(--al-card)]` → transparent).
 *   • On < md viewports the panel opens as a full-screen bottom sheet
 *     via the shared BottomSheet primitive.
 */
import { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Search, Plus, Check } from "lucide-react";
import BottomSheet, { useIsMobile } from "./BottomSheet";

export default function MultiSelectCombo({
  label,
  options = [],       // [{label, count?}] or [{label, count?, state?}]
  values = [],
  onChange,
  onAddNew,           // optional (label) => Promise
  placeholder = "Selecionar…",
  isPt = true,
  testid,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const isMobile = useIsMobile();
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      // Skip when the sheet is portalled — its own backdrop handles close.
      if (isMobile) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [isMobile]);

  // Close on ESC (desktop path — BottomSheet handles it on mobile).
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, isMobile]);

  const q = query.trim().toLowerCase();
  const filtered = options.filter((o) => !q || o.label.toLowerCase().includes(q));

  const toggle = (label) => {
    const next = values.includes(label)
      ? values.filter((v) => v !== label)
      : [...values, label];
    onChange(next);
  };

  const selectAll = () => {
    const set = new Set(values);
    filtered.forEach((o) => set.add(o.label));
    onChange([...set]);
  };
  const clearAll = () => {
    const filteredLabels = new Set(filtered.map((o) => o.label));
    onChange(values.filter((v) => !filteredLabels.has(v)));
  };

  const submitNew = async () => {
    const v = newLabel.trim();
    if (!v || !onAddNew) return;
    setAdding(true);
    try {
      await onAddNew(v);
      onChange([...values, v]);
      setNewLabel("");
    } finally { setAdding(false); }
  };

  const panelBody = (
    <>
      <div className="p-2 border-b border-[var(--al-border)] shrink-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--al-text-muted)]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            autoFocus
            placeholder={isPt ? "Buscar…" : "Search…"}
            className="al-input h-9 w-full pl-7 text-[16px] md:text-[12px]"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-[11px]">
          <button type="button" onClick={selectAll}
            data-testid={`${testid}-select-all`}
            className="text-[#1B3A8A] font-semibold hover:underline">
            {isPt ? "Selecionar todos" : "Select all"} ({filtered.length})
          </button>
          <button type="button" onClick={clearAll}
            data-testid={`${testid}-clear`}
            className="text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
            {isPt ? "Limpar" : "Clear"}
          </button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 min-h-0">
        {filtered.map((o) => {
          const checked = values.includes(o.label);
          return (
            <label key={o.label}
              data-testid={`${testid}-opt-${o.label.replace(/\s+/g, "_")}`}
              className="flex items-center gap-2 px-3 py-2 md:py-1.5 hover:bg-[var(--al-bg)] cursor-pointer text-[14px] md:text-[12px] min-h-[44px] md:min-h-0">
              <span className={`w-4 h-4 md:w-3.5 md:h-3.5 rounded border flex items-center justify-center ${
                checked ? "bg-[#1B3A8A] border-[#1B3A8A]" : "border-[var(--al-border)]"}`}>
                {checked && <Check className="w-2.5 h-2.5 text-white" />}
              </span>
              <input type="checkbox" checked={checked} onChange={() => toggle(o.label)}
                className="sr-only" />
              <span className="flex-1 truncate">{o.label}</span>
              {typeof o.count === "number" && o.count > 0 && (
                <span className="text-[11px] md:text-[10px] text-[var(--al-text-muted)] tabular-nums">
                  {o.count}
                </span>
              )}
            </label>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-[12px] text-[var(--al-text-muted)] py-6">
            {isPt ? "Nenhum resultado" : "No matches"}
          </div>
        )}
      </div>

      {onAddNew && (
        <div className="border-t border-[var(--al-border)] p-2 flex gap-1.5 shrink-0">
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
            placeholder={isPt ? "+ Adicionar novo…" : "+ Add new…"}
            className="al-input h-9 md:h-7 flex-1 text-[16px] md:text-[11.5px]"
            data-testid={`${testid}-newlabel`}
          />
          <button type="button" onClick={submitNew}
            disabled={!newLabel.trim() || adding}
            data-testid={`${testid}-newadd`}
            className="h-9 md:h-7 px-3 md:px-2 rounded bg-[#1B3A8A] text-white text-[13px] md:text-[11px] font-bold disabled:opacity-50 min-w-[44px] md:min-w-0">
            <Plus className="w-4 h-4 md:w-3 md:h-3" />
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="relative" ref={ref} data-testid={testid}
         style={{ isolation: "isolate" }}>
      {label && (
        <label className="text-[10.5px] font-bold uppercase text-[var(--al-text-muted)] block mb-1">
          {label}
        </label>
      )}
      <button type="button" onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        data-testid={`${testid}-toggle`}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="al-input h-11 md:h-9 w-full text-[14px] md:text-[12px] flex items-center justify-between gap-2 text-left disabled:opacity-50">
        <span className="truncate">
          {values.length === 0
            ? <span className="text-[var(--al-text-muted)]">{placeholder}</span>
            : (isPt ? `${values.length} selecionado(s)` : `${values.length} selected`)}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`${testid}-chips`}>
          {values.map((v) => (
            <span key={v}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-[#C9A24E]/10 border border-[#C9A24E]/40 text-[#C9A24E] inline-flex items-center gap-1 max-w-full">
              <span className="truncate max-w-[10rem]">{v}</span>
              <button type="button"
                data-testid={`${testid}-remove-${v.replace(/\s+/g, "_")}`}
                onClick={() => toggle(v)}
                className="shrink-0">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Desktop / tablet: anchored popover */}
      {open && !isMobile && (
        <div
          data-testid={`${testid}-panel`}
          className="absolute left-0 right-0 top-full mt-1 z-50 border border-[var(--al-border)] rounded-lg shadow-xl max-h-72 overflow-hidden flex flex-col"
          style={{
            backgroundColor: "var(--al-card-bg, #FFFFFF)",
            backgroundImage: "none",
          }}
        >
          {panelBody}
        </div>
      )}

      {/* Mobile: full-screen bottom sheet via shared primitive */}
      <BottomSheet
        open={open && isMobile}
        onClose={() => setOpen(false)}
        title={label}
        subtitle={values.length === 0
          ? (isPt ? "Selecionar…" : "Select…")
          : (isPt ? `${values.length} selecionado(s)` : `${values.length} selected`)}
        doneLabel={isPt ? "Concluir" : "Done"}
        testid={`${testid}-sheet`}
      >
        {panelBody}
      </BottomSheet>
    </div>
  );
}
